// ============================================================================
// [SMS_SENDER_PICK_V1] 발신번호 드롭다운 (공용) — 2026-10-31
//
//  보여주는 것 : 두 가비아 계정(oh1656789·oh16567890)에 등록된 발신번호 전부
//                → 백엔드 services/gabiaSenders.js 가 가비아 callback/list 로 받아옴
//  쓰는 곳
//    kind="direct"   SmsDirectSend.jsx  (일반 SMS)  기본 = DIRECT_GABIA_SENDER_PHONE, 저장 안 함
//    kind="property" EstateEditor.jsx   (매물 SMS)  기본 = GABIA_SENDER_PHONE,        저장 안 함
//    kind="contract" SmsRuleManager.jsx (계약 SMS)  기본 = 마지막으로 저장한 번호, 바꾸면 즉시 저장
//  API
//    GET /api/sms-direct/senders?for=direct|property → { list, default }
//    GET /api/contract-sms/sender                    → { list, selected, default }   (admin)
//    PUT /api/contract-sms/sender  { phone }         → 저장                           (admin)
//  고른 값(value)은 숫자만 (예: 01046811253). 비어 있으면 서버가 기본번호로 보냄.
// ============================================================================
import React, { useEffect, useState } from 'react';
import axios from 'axios';

// 로그인 토큰을 직접 붙임 (index.js 의 axios 자동첨부와 중복돼도 무해)
const authH = () => {
  try {
    const t = localStorage.getItem('token');
    return t ? { headers: { Authorization: 'Bearer ' + t } } : {};
  } catch (e) {
    return {};
  }
};

// 01046811253 → 010-4681-1253 / 028785989 → 02-878-5989
const fmt = (v) => {
  const p = String(v || '').replace(/\D/g, '');
  if (p.startsWith('02')) return p.replace(/^(02)(\d{3,4})(\d{4})$/, '$1-$2-$3');
  return p.replace(/^(\d{3})(\d{3,4})(\d{4})$/, '$1-$2-$3');
};

export default function SenderSelect({ kind = 'direct', value, onChange, style }) {
  const isCt = kind === 'contract';
  const [list, setList] = useState([]);
  const [def, setDef] = useState('');
  const [err, setErr] = useState('');
  const [saveMsg, setSaveMsg] = useState('');

  // 처음 한 번 목록과 기본번호를 받아, 부모의 값이 비어 있으면 기본(계약은 저장값)으로 채운다
  useEffect(() => {
    let alive = true;
    const url = isCt ? '/api/contract-sms/sender' : '/api/sms-direct/senders?for=' + kind;
    axios.get(url, authH())
      .then((r) => {
        if (!alive) return;
        const d = r.data || {};
        // [SMS_SENDER_PICK_V2] 옛 서버(HTML 응답)·가비아 조회 실패(빈 목록)면 기다리지 않고 바로 안내
        if (typeof d !== 'object' || !Array.isArray(d.list)) { setErr('서버가 목록을 주지 않음 — 백엔드 재시작 확인'); return; }
        if (!d.list.length) { setErr('가비아에서 발신번호를 받지 못함'); return; }
        setList(d.list);
        setDef(d.default || '');
        const init = isCt ? (d.selected || d.default) : d.default;
        if (!value && init && onChange) onChange(init);
      })
      .catch((e) => {
        if (alive) setErr((e.response && e.response.data && e.response.data.message) || e.message);
      });
    return () => { alive = false; };
  }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const change = async (v) => {
    if (onChange) onChange(v);
    if (!isCt) return;                    // 매물·일반은 이번 발송에만 쓰고 저장하지 않음
    setSaveMsg('저장 중...');
    try {
      await axios.put('/api/contract-sms/sender', { phone: v }, authH());
      setSaveMsg('✅ 저장됨 — 이후 계약 문자는 이 번호로 발송');
    } catch (e) {
      setSaveMsg('❌ ' + ((e.response && e.response.data && e.response.data.message) || e.message));
    }
  };

  if (err) {
    return (
      <div style={{ fontSize: '12px', color: '#b45309', ...style }}>
        발신번호 목록을 불러오지 못했습니다 — 기본 번호로 발송됩니다 ({err})
      </div>
    );
  }
  if (!list.length) {
    return <div style={{ fontSize: '12px', color: '#94a3b8', ...style }}>발신번호 불러오는 중...</div>;
  }

  // 저장된 번호가 가비아 목록에서 빠진 경우에도 선택칸에 보이게
  const opts = !value || list.some((x) => x.phone === value)
    ? list
    : [{ phone: value, name: '(가비아 목록에 없음)', account: '' }, ...list];

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', fontSize: '12px', ...style }}>
      <span style={{ fontWeight: 600 }}>📞 발신번호</span>
      <select
        value={value || ''}
        onChange={(e) => change(e.target.value)}
        style={{ padding: '5px', border: '1px solid #e2e8f0', borderRadius: '3px', fontSize: '12px', maxWidth: '100%' }}
      >
        {opts.map((x) => (
          <option key={x.phone} value={x.phone}>
            {fmt(x.phone)} · {x.name}{x.account ? ' (' + x.account + ')' : ''}{x.phone === def ? ' — 기본' : ''}
          </option>
        ))}
      </select>
      {isCt && saveMsg ? <span>{saveMsg}</span> : null}
    </div>
  );
}
